// ØVINGSMANUSET FOR NJORD — OPPDIKTET. Aldri på uten at operatøren har slått
// det på selv, og aldri i det hele tatt på en post med en konfigurert bro.
//
// ══ HVA SOM ER HER, OG HVA SOM BOR I PAKKEN ════════════════════════════════
// Mekanikken — flagget som følger dataen, porten som nekter øving på en post med
// bro, vakta på den utgående køen, kilden som kjører manuset — ligger i
// `@skade/sensorflate/ovelse.ts`, delt med ULL. Les toppkommentaren der først;
// de fire ufravikelige kravene står i sin helhet bare der.
//
// Denne fila er MANUSET: hva en kystobservasjonspost faktisk måler. Det kan bare
// NJORD vite, og derfor ligger det her.
//
// ══ TALLENE ER VALGT FOR Å PRØVE FLATA, IKKE FOR Å SE REALISTISKE UT ═══════
// Grunnloven sier tre ting om en sensorflate, og manuset er bygget for å avsløre
// flata hvis den bryter dem:
//
//   * ALDRI DIKT OPP EN POSISJON. Kontakt 04 har ingen `lat`/`lon` i det hele
//     tatt — bare peilinger. Kontaktkortet SKAL si «fusjonen har ikke løst hvor
//     den er», og kartet skal ikke tegne et punkt. Kontakt 01 har posisjon men
//     `posisjonLost: false`, altså PROJISERT fra én peiling — den skal leses som
//     en antakelse om avstand, ikke som et målt punkt.
//   * ALLTID VIS ALDER. Kontakt 03 er 78 s gammel og skal SE gammel ut.
//     Hydrofonen er degradert, med en grunn som står ordrett.
//   * SKILL «SER DEN IKKE» FRA «MÅLER IKKE». Kontakt 01 (USV, 28 knop) ses av
//     RF og hydrofon, men IKKE av radaren — og radaren MÅLER, den er frisk. Det
//     er signaturen til et lite, lavt mål, og den viktigste raden i hele kortet.
//
// ══ HVORFOR EN PULS, OG IKKE BARE ETT ØYEBLIKKSBILDE ═══════════════════════
// Den forrige demokilden sendte alt ÉN gang. Da vokser hver alder monotont, og
// etter to minutter ser hele posten død ut — man kan øve på «alt er borte» og på
// ingenting annet. Pulsen mater derfor nye observasjoner mens øvingen går, og den
// er bevisst SKJEV:
//
//   * RF-en fortsetter å peile kontakt 01. Radaren gjør IKKE det. Skillet
//     «måler, men ser ikke» holder seg dermed levende i stedet for å vaskes ut.
//   * Hydrofonen melder sjeldnere enn RF-en (hvert fjerde tikk), så «sen» og
//     «fersk» kan stå side om side i stolpen.
//   * Kameraet melder ALDRI i pulsen, og kontakt 03 heller ikke. De skal gå fra
//     fersk til sen til stille til borte mens man ser på — den ENE overgangen
//     operatøren må kjenne igjen i mørket.
//
// En operatør som øver på perfekt data lærer feil. Ingenting i pulsen fyller ut
// et hull manuset har laget.
import type { KopMelding, KopPost, KopSensor, Kystkontakt, Observasjon } from './typer.js';

/** Posten manuset later som det kommer fra. Oslofjorden, ytre Hvaler. */
const OVELSE_POST: KopPost = {
  id: 'ØVING-KOP-1',
  navn: 'ØVING — Kystobservasjonspost Torbjørnskjær',
  lat: 59.0567,
  lon: 10.7583,
  distrikt: 'HV-01',
  omrade: 'Follo',
};

/**
 * Alle navn begynner med «ØVING». En operatør som roper et kontaktnavn over
 * sambandet roper altså ordet «øving» først — det er den ene merkingen som
 * virker også når skjermen ikke er i rommet.
 */
const OVELSE_SENSORER: KopSensor[] = [
  {
    id: 'ovelse-radar-1', art: 'radar', navn: 'ØVING Radar Nord', postId: OVELSE_POST.id,
    lat: OVELSE_POST.lat, lon: OVELSE_POST.lon, rekkeviddeM: 12_000, tilstand: 'ok',
    forventetIntervallMs: 2500,
  },
  {
    id: 'ovelse-rf-1', art: 'rf', navn: 'ØVING KrakenRF Vest', postId: OVELSE_POST.id,
    lat: OVELSE_POST.lat, lon: OVELSE_POST.lon, rekkeviddeM: 25_000, tilstand: 'ok',
    forventetIntervallMs: 8000,
  },
  {
    id: 'ovelse-hyd-1', art: 'hydrofon', navn: 'ØVING Hydrofon Søndre', postId: OVELSE_POST.id,
    lat: 59.0521, lon: 10.7612, rekkeviddeM: 6000, tilstand: 'degradert',
    merknad: 'Forhøyet bakgrunnsstøy — sjøgang 4',
    forventetIntervallMs: 3000,
  },
  {
    id: 'ovelse-ptz-1', art: 'kamera', navn: 'ØVING PTZ Mast', postId: OVELSE_POST.id,
    lat: OVELSE_POST.lat, lon: OVELSE_POST.lon, peilingDeg: 205, apningDeg: 12,
    rekkeviddeM: 4000, tilstand: 'ok', forventetIntervallMs: 1000,
  },
];

let teller = 0;
function obs(
  sensorId: string,
  kontaktId: string,
  tsMs: number,
  maaling: Observasjon['maaling'],
  sikkerhet?: number,
): Observasjon {
  return { id: `ovelse-obs-${++teller}`, sensorId, kontaktId, tsMs, maaling, sikkerhet };
}

/**
 * Bygg øvingsbildet. `naaMs` sendes inn (ikke `Date.now()` innvendig) slik at
 * aldrene er DETERMINISTISKE — ellers kan man ikke se om «stille i 40 s» faktisk
 * ser stille ut, som er det denne dataen først og fremst skal vise.
 *
 * MERK at ingenting her setter `ovelse: true` for hånd. Øvingskilden stempler
 * hver melding på vei ut (`lagOvelseskilde`), og lageret bærer stempelet ned på
 * entitetene. Å sette det her i tillegg ville gitt to steder å glemme det.
 */
export function ovelsemeldinger(naaMs: number): KopMelding[] {
  const kontakter: Kystkontakt[] = [
    {
      // Den viktige: liten, rask, og radaren ser den IKKE. Kontaktkortet skal
      // vise «Måler, men ser ikke kontakten» på radar-raden.
      id: 'ovelse-k-usv', postId: OVELSE_POST.id, navn: 'ØVING Kontakt 01',
      tilhorighet: 'unknown', klasse: 'usv',
      lat: 59.0421, lon: 10.7401, posisjonLost: false,
      sisteTsMs: naaMs - 3000, fartKn: 28, kursDeg: 18, flagg: 'ingen',
      observasjoner: [
        obs('ovelse-rf-1', 'ovelse-k-usv', naaMs - 3000, {
          slag: 'frekvens', frekvensHz: 2_437_000_000, bandbreddeHz: 20_000_000,
          modulasjon: 'OFDM (kontrollenke-lignende)', signalDbm: -71, peilingDeg: 214, usikkerhetDeg: 4,
        }, 0.72),
        obs('ovelse-hyd-1', 'ovelse-k-usv', naaMs - 9000, {
          slag: 'lydsignatur', signaturNavn: 'Vannjet, høyt turtall', snrDb: 11, peilingDeg: 211, usikkerhetDeg: 9,
        }, 0.58),
      ],
    },
    {
      // Full dekning: alle fire ser den. Skal lese som «Sterk kontakt».
      id: 'ovelse-k-rib', postId: OVELSE_POST.id, navn: 'ØVING Kontakt 02',
      tilhorighet: 'unknown', klasse: 'rib',
      lat: 59.0489, lon: 10.7702, posisjonLost: true,
      sisteTsMs: naaMs - 1500, fartKn: 31, kursDeg: 342, flagg: 'folg',
      observasjoner: [
        obs('ovelse-radar-1', 'ovelse-k-rib', naaMs - 1500, {
          slag: 'peiling-avstand', peilingDeg: 168, usikkerhetDeg: 0.6, avstandM: 1180, usikkerhetM: 25,
        }, 0.91),
        obs('ovelse-rf-1', 'ovelse-k-rib', naaMs - 6000, {
          slag: 'frekvens', frekvensHz: 156_800_000, bandbreddeHz: 25_000,
          modulasjon: 'FM (VHF kanal 16)', signalDbm: -58, peilingDeg: 170, usikkerhetDeg: 5,
        }, 0.8),
        obs('ovelse-hyd-1', 'ovelse-k-rib', naaMs - 4000, {
          slag: 'lydsignatur', signaturNavn: 'Utenbords, 2 motorer', snrDb: 17, peilingDeg: 167, usikkerhetDeg: 8,
        }, 0.66),
        // Ingen `bildeUrl`: øvingsdata skal ikke finne opp et bilde. Kortet og
        // videoruta viser da «bilde ikke levert», som er sannheten både under
        // øving og den dagen en ekte bro er treg med stillbildene.
        obs('ovelse-ptz-1', 'ovelse-k-rib', naaMs - 2000, {
          slag: 'bilde', peilingDeg: 168, zoom: 14, klassifisering: 'Åpen rib, 2–3 personer',
        }, 0.7),
      ],
    },
    {
      // Bare radaren, og stille i over et minutt: skal SE gammel ut i lista.
      id: 'ovelse-k-skip', postId: OVELSE_POST.id, navn: 'ØVING Kontakt 03',
      tilhorighet: 'neutral', klasse: 'storfartoy',
      lat: 59.0912, lon: 10.8104, posisjonLost: true,
      sisteTsMs: naaMs - 78_000, fartKn: 11, kursDeg: 205, flagg: 'ingen',
      observasjoner: [
        obs('ovelse-radar-1', 'ovelse-k-skip', naaMs - 78_000, {
          slag: 'peiling-avstand', peilingDeg: 31, usikkerhetDeg: 0.6, avstandM: 5340, usikkerhetM: 25,
        }, 0.95),
      ],
    },
    {
      // ALDRI DIKT OPP EN POSISJON. To peilinger fra to sensorer, ingen avstand
      // fra noen av dem, og altså INGEN `lat`/`lon`. Fusjonen har ikke løst hvor
      // den er, og flata skal si nettopp det — ikke tegne et punkt der
      // peilingene tilfeldigvis krysser.
      id: 'ovelse-k-uloest', postId: OVELSE_POST.id, navn: 'ØVING Kontakt 04 — UTEN POSISJON',
      tilhorighet: 'unknown', klasse: 'ukjent',
      posisjonLost: false,
      sisteTsMs: naaMs - 12_000, flagg: 'ingen',
      observasjoner: [
        obs('ovelse-rf-1', 'ovelse-k-uloest', naaMs - 12_000, {
          slag: 'frekvens', frekvensHz: 433_920_000, bandbreddeHz: 250_000,
          modulasjon: 'Ukjent, kort burst', signalDbm: -88, peilingDeg: 243, usikkerhetDeg: 11,
        }, 0.31),
        obs('ovelse-hyd-1', 'ovelse-k-uloest', naaMs - 26_000, {
          // Uten `peilingDeg`: hydrofonen HØRTE noe, men løste ikke retningen.
          // En observasjon uten retning er fortsatt en observasjon.
          slag: 'lydsignatur', signaturNavn: 'Svak propell, ubestemt', snrDb: 4,
        }, 0.22),
      ],
    },
  ];

  return [
    { type: 'post', post: OVELSE_POST },
    ...OVELSE_SENSORER.map((sensor): KopMelding => ({ type: 'sensor', sensor })),
    ...kontakter.map((kontakt): KopMelding => ({ type: 'kontakt', kontakt })),
  ];
}

/**
 * Pulsen — nye målinger mens øvingen går. Se filens toppkommentar for hvorfor
 * den er skjev, og hvilke hull den bevisst IKKE fyller.
 *
 * `tikk` teller fra 1. Alt her er observasjoner og ikke hele kontakter, fordi det
 * er slik en ekte bro oppfører seg mellom fusjonene: målingene kommer først, og
 * kontakten oppdateres når fusjonen har regnet på dem.
 */
export function ovelsepuls(naaMs: number, tikk: number): KopMelding[] {
  const ut: KopMelding[] = [];

  // RF-en holder kontakt 01. Peilingen VANDRER (18° kurs, 28 knop), og
  // usikkerheten står stille — den blir ikke bedre av at det kommer flere
  // målinger fra samme antenne.
  ut.push({
    type: 'observasjon',
    observasjon: obs('ovelse-rf-1', 'ovelse-k-usv', naaMs, {
      slag: 'frekvens', frekvensHz: 2_437_000_000, bandbreddeHz: 20_000_000,
      modulasjon: 'OFDM (kontrollenke-lignende)', signalDbm: -71 + (tikk % 5),
      peilingDeg: (214 + tikk * 0.7) % 360, usikkerhetDeg: 4,
    }, 0.72),
  });

  // Hydrofonen hvert fjerde tikk: den skal kunne stå «sen» ved siden av en fersk
  // RF-rad, slik at de to alderskolonnene faktisk betyr noe hver for seg.
  if (tikk % 4 === 0) {
    ut.push({
      type: 'observasjon',
      observasjon: obs('ovelse-hyd-1', 'ovelse-k-usv', naaMs, {
        slag: 'lydsignatur', signaturNavn: 'Vannjet, høyt turtall',
        snrDb: 9 + (tikk % 3), peilingDeg: 211, usikkerhetDeg: 9,
      }, 0.5),
    });
  }

  // Radaren melder på RIB-en, aldri på USV-en. Det er ikke en forglemmelse: det
  // er hele skillet mellom «sensoren måler ikke» og «sensoren ser den ikke», og
  // det skal holde seg levende gjennom hele øvingen.
  ut.push({
    type: 'observasjon',
    observasjon: obs('ovelse-radar-1', 'ovelse-k-rib', naaMs, {
      slag: 'peiling-avstand', peilingDeg: (168 + tikk * 1.3) % 360, usikkerhetDeg: 0.6,
      avstandM: Math.max(120, 1180 - tikk * 12), usikkerhetM: 25,
    }, 0.91),
  });

  return ut;
}

/**
 * Postens øvingsposisjon — kartet trenger et startpunkt.
 *
 * MERK at dette er POSTENS egen plassering, ikke en kontakts. Å utlede et
 * kartsenter av en oppdiktet kontakt ville vært å bruke en oppdiktet posisjon til
 * noe.
 */
export const OVELSE_SENTER = { lat: OVELSE_POST.lat!, lon: OVELSE_POST.lon! };
